"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReplayData } from "@/lib/demo-parser";
import { advanceReplayTick, resolveReplayTickSample } from "@/lib/replay-playback";

interface UseReplayPlaybackOptions {
  replayData: ReplayData | null;
  initialTick?: number | null;
  isActive?: boolean;
  /** Called on every animation frame with the new frame index. Use for direct canvas drawing. */
  onFrameChange?: (frameIndex: number) => void;
  /** Called on every browser animation frame so WebGL can interpolate parsed samples smoothly. */
  onPlaybackFrame?: (frameIndex: number, nextFrameIndex: number, alpha: number) => void;
}

/**
 * Manages replay playback with ref-based animation (no React re-renders per frame).
 * UI state (`displayFrame`) is throttled to ~2 FPS for kill feed, stats panels, etc.
 */
export function useReplayPlayback({
  replayData,
  initialTick = null,
  isActive = true,
  onFrameChange,
  onPlaybackFrame,
}: UseReplayPlaybackOptions) {
  const frameRef = useRef(0);
  const [displayFrame, setDisplayFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const animationFrameRef = useRef<number | null>(null);
  const playbackTickRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const lastDisplayUpdateRef = useRef(0);
  const previousReplayRef = useRef<ReplayData | null>(null);
  const previousInitialTickRef = useRef<number | null>(initialTick);

  const totalFrames = replayData?.frames.length ?? 0;

  // Preserve the current tick while progressively loaded rounds are appended.
  // A different focus tick or a non-prefix replay still starts a fresh session.
  useEffect(() => {
    const previousReplay = previousReplayRef.current;
    const previousInitialTick = previousInitialTickRef.current;
    const isProgressiveAppend = Boolean(
      previousReplay &&
        replayData &&
        previousReplay.mapName === replayData.mapName &&
        previousInitialTick === initialTick &&
        previousReplay.frames.length <= replayData.frames.length &&
        previousReplay.frames[0]?.tick === replayData.frames[0]?.tick &&
        previousReplay.frames.at(-1)?.tick ===
          replayData.frames[previousReplay.frames.length - 1]?.tick
    );
    let initialFrame = 0;
    if (isProgressiveAppend && replayData) {
      initialFrame = resolveReplayTickSample(replayData.frames, playbackTickRef.current).frameIndex;
    } else if (replayData && initialTick != null && replayData.frames.length > 0) {
      let low = 0;
      let high = replayData.frames.length - 1;
      while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (replayData.frames[middle].tick < initialTick) low = middle + 1;
        else high = middle;
      }
      initialFrame = low;
      if (
        low > 0 &&
        Math.abs(replayData.frames[low - 1].tick - initialTick) <
          Math.abs(replayData.frames[low].tick - initialTick)
      ) {
        initialFrame = low - 1;
      }
    }
    frameRef.current = initialFrame;
    playbackTickRef.current = isProgressiveAppend
      ? Math.max(
          replayData?.frames[0]?.tick ?? 0,
          Math.min(replayData?.frames.at(-1)?.tick ?? 0, playbackTickRef.current),
        )
      : replayData?.frames[initialFrame]?.tick ?? 0;
    lastFrameTimeRef.current = 0;
    lastDisplayUpdateRef.current = 0;
    previousReplayRef.current = replayData;
    previousInitialTickRef.current = initialTick;

    const resetTimer = window.setTimeout(() => {
      setDisplayFrame(initialFrame);
      if (!isProgressiveAppend) setIsPlaying(false);
      onFrameChange?.(initialFrame);
      const sample = replayData
        ? resolveReplayTickSample(replayData.frames, playbackTickRef.current)
        : { frameIndex: initialFrame, nextFrameIndex: initialFrame, alpha: 0 };
      onPlaybackFrame?.(sample.frameIndex, sample.nextFrameIndex, sample.alpha);
    }, 0);

    return () => {
      window.clearTimeout(resetTimer);
    };
  }, [initialTick, onFrameChange, onPlaybackFrame, replayData]);

  useEffect(() => {
    if (!isActive) setIsPlaying(false);
  }, [isActive]);

  // Sync displayFrame when not playing (for seek operations)
  const syncDisplay = useCallback(() => {
    setDisplayFrame(frameRef.current);
    onFrameChange?.(frameRef.current);
    const sample = replayData
      ? resolveReplayTickSample(replayData.frames, playbackTickRef.current)
      : { frameIndex: frameRef.current, nextFrameIndex: frameRef.current, alpha: 0 };
    onPlaybackFrame?.(sample.frameIndex, sample.nextFrameIndex, sample.alpha);
  }, [onFrameChange, onPlaybackFrame, replayData]);

  // Animation loop — updates frameRef (no re-render), throttles displayFrame
  useEffect(() => {
    if (!isActive || !isPlaying || !replayData) return;

    const animate = (currentTime: number) => {
      if (!lastFrameTimeRef.current) {
        lastFrameTimeRef.current = currentTime;
        lastDisplayUpdateRef.current = currentTime;
      }

      const elapsed = Math.max(0, currentTime - lastFrameTimeRef.current);
      lastFrameTimeRef.current = currentTime;
      const lastTick = replayData.frames.at(-1)?.tick ?? 0;
      playbackTickRef.current = Math.min(
        lastTick,
        advanceReplayTick(
          playbackTickRef.current,
          elapsed,
          replayData.tickRate,
          playbackSpeed,
        ),
      );
      const sample = resolveReplayTickSample(replayData.frames, playbackTickRef.current);
      if (sample.frameIndex !== frameRef.current) {
        frameRef.current = sample.frameIndex;
        onFrameChange?.(sample.frameIndex);
      }
      onPlaybackFrame?.(sample.frameIndex, sample.nextFrameIndex, sample.alpha);

      // Throttle React state updates to ~2 FPS (every 500ms)
      if (currentTime - lastDisplayUpdateRef.current >= 500) {
        setDisplayFrame(sample.frameIndex);
        lastDisplayUpdateRef.current = currentTime;
      }

      if (playbackTickRef.current >= lastTick) {
        setDisplayFrame(sample.frameIndex);
        setIsPlaying(false);
        return;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, isPlaying, replayData, playbackSpeed, onFrameChange, onPlaybackFrame]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying((prev) => {
      if (!prev) {
        lastFrameTimeRef.current = 0;
        lastDisplayUpdateRef.current = 0;
      }
      return !prev;
    });
  }, []);

  const play = useCallback(() => {
    lastFrameTimeRef.current = 0;
    lastDisplayUpdateRef.current = 0;
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const setSpeed = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
  }, []);

  const skipForward = useCallback(() => {
    if (!replayData) return;
    frameRef.current = Math.min(replayData.frames.length - 1, frameRef.current + 50);
    playbackTickRef.current = replayData.frames[frameRef.current]?.tick ?? playbackTickRef.current;
    syncDisplay();
  }, [replayData, syncDisplay]);

  const skipBackward = useCallback(() => {
    frameRef.current = Math.max(0, frameRef.current - 50);
    playbackTickRef.current = replayData?.frames[frameRef.current]?.tick ?? playbackTickRef.current;
    syncDisplay();
  }, [replayData, syncDisplay]);

  const seekToFrame = useCallback(
    (frame: number) => {
      if (!replayData) return;
      frameRef.current = Math.max(0, Math.min(replayData.frames.length - 1, frame));
      playbackTickRef.current = replayData.frames[frameRef.current]?.tick ?? playbackTickRef.current;
      syncDisplay();
    },
    [replayData, syncDisplay]
  );

  const seekToRound = useCallback(
    (roundNumber: number) => {
      if (!replayData) return;
      const frameIndex = replayData.frames.findIndex(
        (frame) => frame.roundNumber === roundNumber
      );
      if (frameIndex !== -1) {
        frameRef.current = frameIndex;
        playbackTickRef.current = replayData.frames[frameIndex]?.tick ?? playbackTickRef.current;
        setIsPlaying(false);
        syncDisplay();
      }
    },
    [replayData, syncDisplay]
  );

  return {
    frameRef,
    displayFrame,
    isPlaying,
    playbackSpeed,
    play,
    pause,
    togglePlayPause,
    setSpeed,
    skipForward,
    skipBackward,
    seekToFrame,
    seekToRound,
    totalFrames,
  };
}
